import { useCartInfo } from "@contexts/CartInfoContext";
import { useSetupIntent } from "@hooks/useSetupIntent";
import {
  Box,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Typography,
} from "@mui/material";
import { useGetStripePaymentMethods } from "@queries/private/useGetStripePaymentMethods";
import { useGetStripeSetupIntentClientSecret } from "@queries/useGetStripeSetupIntentClientSecret";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import type { SupportedCurrency } from "@utils/currencyUtils";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import LoadingOverlay from "../LoadingOverlay";
import ConfirmAuthenticated from "./ConfirmAuthenticated";

interface ConfirmPurchaseProps {
  hasAttemptedValidation?: boolean;
  title?: string;
}

export default function ConfirmAuthenticatedContainer({ title }: ConfirmPurchaseProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [cardError, setCardError] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [loadingMessage] = useState<string>("Processing...");

  const { control, watch, setValue } = useFormContext();

  const {
    data: paymentMethodsData,
    isLoading: isLoadingPaymentMethods,
    error: paymentMethodsError,
    refetch: refetchPaymentMethods,
  } = useGetStripePaymentMethods();

  const savedPaymentMethods = (paymentMethodsData as any)?.stripePaymentMethods || [];
  const defaultPaymentMethodId = (paymentMethodsData as any)?.defaultStripePaymentMethodId || null;
  const displayPaymentMethodId =
    selectedPaymentMethod && selectedPaymentMethod !== "new_card"
      ? selectedPaymentMethod
      : (defaultPaymentMethodId ?? null);
  const displayCardData = savedPaymentMethods.find(
    (card: any) => card.id === displayPaymentMethodId,
  )?.card;

  const uuid = watch("uuid");
  const clientSecret = watch("stripeClientSecret");

  // Get cart info from context - CartInfoContext handles all updatePurchase calls
  const { cartInfo, isLoading: isCartLoading, isTaxLoading, resetForConfirmPage } = useCartInfo();
  const selectedCurrency =
    (cartInfo?.currency as SupportedCurrency) ??
    (watch("selectedCurrency") as SupportedCurrency) ??
    "usd";
  const totalWithTax = cartInfo?.total_amount;

  const { refetch: getStripeSetupIntentClientSecret } = useGetStripeSetupIntentClientSecret(uuid);

  // Reset cart info on mount - clears stale server data so tax recalculates fresh
  useEffect(() => {
    resetForConfirmPage();
  }, [resetForConfirmPage]);

  // Fetch payment methods and set default on mount
  useEffect(() => {
    if (uuid) {
      refetchPaymentMethods().then((result) => {
        if (result.data) {
          const data = result.data as any;
          if (data.defaultStripePaymentMethodId && data.stripePaymentMethods.length > 0) {
            setSelectedPaymentMethod(data.defaultStripePaymentMethodId);
            setValue("stripePaymentMethodId", data.defaultStripePaymentMethodId);
            // Extract and set billing postal code from default method
            const defaultMethod = data.stripePaymentMethods.find(
              (m: any) => m.id === data.defaultStripePaymentMethodId,
            );
            if (defaultMethod?.billing_details?.address?.postal_code) {
              setValue("billingPostalCode", defaultMethod.billing_details.address.postal_code);
            }
          }
        }
      });
    }
  }, [uuid, refetchPaymentMethods, setValue]);

  // Sync selection when a new payment method is created via the Add card flow
  const formStripePaymentMethodId = watch("stripePaymentMethodId");
  useEffect(() => {
    if (formStripePaymentMethodId && formStripePaymentMethodId !== selectedPaymentMethod) {
      setSelectedPaymentMethod(formStripePaymentMethodId);
      // Refresh payment methods to fetch the new card details for display
      refetchPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStripePaymentMethodId]);

  // Fetch client secret for new card setup
  useEffect(() => {
    if (uuid && !clientSecret) {
      getStripeSetupIntentClientSecret().then((res) => {
        setValue("stripeClientSecret", res.data?.data);
      });
    }
  }, [uuid, clientSecret, getStripeSetupIntentClientSecret, setValue]);

  const hasEffectiveSavedMethod = !!displayPaymentMethodId && displayPaymentMethodId !== "new_card";

  const handlePaymentMethodSelection = (paymentMethodId: string | null) => {
    setSelectedPaymentMethod(paymentMethodId);

    if (paymentMethodId && paymentMethodId !== "new_card") {
      setValue("stripePaymentMethodId", paymentMethodId);
      // Extract and set billing postal code from saved method - this triggers CartInfoContext updatePurchase
      const selectedMethod = savedPaymentMethods.find((m: any) => m.id === paymentMethodId);
      if (selectedMethod?.billing_details?.address?.postal_code) {
        setValue("billingPostalCode", selectedMethod.billing_details.address.postal_code);
      }
      setCardError(null);
    } else {
      setValue("stripePaymentMethodId", "");
    }
    refetchPaymentMethods();
  };

  const handleAgreementChange = async (isChecked: boolean, field: any) => {
    field.onChange(isChecked);
    // CartInfoContext handles updatePurchase when billingPostalCode is set
    // Just validate that we have a payment method
    if (isChecked && !hasEffectiveSavedMethod) {
      setCardError("Please select or add a payment method.");
    } else {
      setCardError(null);
    }
  };

  const getClientSecret = async (): Promise<string | null> => {
    const res = await getStripeSetupIntentClientSecret();
    return res.data?.data ?? null;
  };

  const { handleCreditCardChange } = useSetupIntent({
    stripe,
    elements,
    getClientSecret,
    setValue,
    setCardError,
    resetAgreementOnChange: false,
  });

  const isLoadingOverlay = isCartLoading || isTaxLoading;

  const handleBilling = () => {
    window.parent.postMessage({ action: "navigateToBilling" }, "*");
  };

  return (
    <Box>
      <CardHeader title={title} slotProps={{ title: { variant: "h5" } }} />

      <CardContent>
        <Typography>
          We will use the default payment method we have on file for you, or you can add a new one.
          You can also manage your saved payment methods on the{" "}
          <button
            type="button"
            onClick={handleBilling}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            Billing
          </button>{" "}
          page
        </Typography>
      </CardContent>

      <CardContent>
        <ConfirmAuthenticated
          selectedPaymentMethod={selectedPaymentMethod}
          control={control}
          handleAgreementChange={handleAgreementChange}
          loading={isLoadingOverlay}
          isAuthUpdatingPurchase={isCartLoading || isTaxLoading}
          displayCard={displayCardData}
          isPaymentDialogOpen={isPaymentDialogOpen}
          onOpenDialog={() => setIsPaymentDialogOpen(true)}
          onCloseDialog={() => setIsPaymentDialogOpen(false)}
          handleSavedMethodChange={handlePaymentMethodSelection}
          defaultPaymentMethodId={displayPaymentMethodId ?? null}
          savedPaymentMethods={savedPaymentMethods}
          isLoadingPaymentMethods={isLoadingPaymentMethods}
          paymentMethodsError={paymentMethodsError}
          cardError={cardError}
          handleCreditCardChange={handleCreditCardChange}
          refetchPaymentMethods={async () => {
            await refetchPaymentMethods();
          }}
          totalWithTax={totalWithTax}
          selectedCurrency={selectedCurrency}
        />
      </CardContent>

      {cardError && (
        <FormControl error fullWidth sx={{ mt: 1 }}>
          <FormHelperText>
            Sorry, we had an issue adding your payment method. For reference: {cardError}
          </FormHelperText>
        </FormControl>
      )}

      <LoadingOverlay open={isLoadingOverlay} message={loadingMessage} />
    </Box>
  );
}
